import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Building2,
  Users, 
  Shield, 
  User, 
  Power,
  PowerOff,
  Search,
  Filter,
  FileText,
  UserCheck,
  UserX,
  Globe,
  Mail,
  Plus,
  Trash2,
  AlertTriangle,
  RotateCcw,
  Copy,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  super_admin: {
    label: 'Super Admin',
    color: 'bg-red-100 text-red-800'
  },
  admin: {
    label: 'Administrador',
    color: 'bg-blue-100 text-blue-800'
  },
  therapist: {
    label: 'Terapeuta',
    color: 'bg-green-100 text-green-800'
  },
  read_only: {
    label: 'Solo Lectura',
    color: 'bg-gray-100 text-gray-800'
  }
};

const GlobalAdminPanelPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [selectedInstitution, setSelectedInstitution] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Invite user modal
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'therapist',
    institution_id: '',
    phone: '',
    send_email: true
  });
  const [inviting, setInviting] = useState(false);
  const [createdInvite, setCreatedInvite] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || '');
    toast.success('Copiado al portapapeles');
  };

  const markShared = async (id) => {
    if (!id) return;
    try {
      await axios.post(`${API}/invitations/${id}/mark-shared`, {}, { withCredentials: true });
    } catch (error) {
      // non-blocking
    }
  };

  const buildWhatsappUrl = (phone, message) => {
    const digits = (phone || '').replace(/\D/g, '');
    return digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  // Create institution modal
  const [instDialogOpen, setInstDialogOpen] = useState(false);
  const [instForm, setInstForm] = useState({
    name: '',
    address: '',
    phone: '',
    admin_name: '',
    admin_email: '',
    admin_password: ''
  });
  const [creatingInst, setCreatingInst] = useState(false);

  // Delete institution modal
  const [deleteInstDialog, setDeleteInstDialog] = useState({ open: false, inst: null });
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deletingInst, setDeletingInst] = useState(false);

  // Recycle bin
  const [trash, setTrash] = useState([]);
  const [purgeDialog, setPurgeDialog] = useState({ open: false, inst: null });
  const [purgeConfirmName, setPurgeConfirmName] = useState('');
  const [purging, setPurging] = useState(false);

  const fetchTrash = async () => {
    try {
      const res = await axios.get(`${API}/admin/institutions/trash`, { withCredentials: true });
      setTrash(res.data);
    } catch (error) {
      // silent
    }
  };

  const handleRestoreInstitution = async (inst) => {
    if (!window.confirm(`¿Restaurar la institución "${inst.name}"?`)) return;
    try {
      await axios.post(`${API}/admin/institutions/${inst.id}/restore`, {}, { withCredentials: true });
      toast.success(`Institución "${inst.name}" restaurada`);
      fetchData();
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar institución');
    }
  };

  const handlePurgeInstitution = async () => {
    const inst = purgeDialog.inst;
    if (!inst) return;
    setPurging(true);
    try {
      await axios.delete(`${API}/admin/institutions/${inst.id}/purge`, {
        withCredentials: true,
        data: { confirm_name: purgeConfirmName }
      });
      toast.success(`Institución "${inst.name}" eliminada permanentemente`);
      setPurgeDialog({ open: false, inst: null });
      setPurgeConfirmName('');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar institución');
    } finally {
      setPurging(false);
    }
  };

  const handleToggleInstitution = async (inst) => {
    const newActive = !(inst.is_active !== false);
    const action = newActive ? 'activar' : 'desactivar';
    if (!window.confirm(`¿Seguro que deseas ${action} la institución "${inst.name}"? ${newActive ? '' : 'Sus usuarios no podrán iniciar sesión.'}`)) return;
    try {
      await axios.put(
        `${API}/admin/institutions/${inst.id}/status`,
        { is_active: newActive },
        { withCredentials: true }
      );
      toast.success(`Institución ${newActive ? 'activada' : 'desactivada'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al ${action} institución`);
    }
  };

  const handleDeleteInstitution = async () => {
    const inst = deleteInstDialog.inst;
    if (!inst) return;
    setDeletingInst(true);
    try {
      await axios.delete(`${API}/admin/institutions/${inst.id}`, {
        withCredentials: true,
        data: { confirm_name: deleteConfirmName }
      });
      toast.success(`Institución "${inst.name}" movida a la papelera`);
      setDeleteInstDialog({ open: false, inst: null });
      setDeleteConfirmName('');
      fetchData();
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar institución');
    } finally {
      setDeletingInst(false);
    }
  };

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    setCreatingInst(true);
    try {
      await axios.post(
        `${API}/admin/institutions`,
        {
          name: instForm.name,
          address: instForm.address || null,
          phone: instForm.phone || null,
          admin_name: instForm.admin_name,
          admin_email: instForm.admin_email,
          admin_password: instForm.admin_password
        },
        { withCredentials: true }
      );
      toast.success('Institución creada correctamente con su administrador');
      setInstDialogOpen(false);
      setInstForm({ name: '', address: '', phone: '', admin_name: '', admin_email: '', admin_password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la institución');
    } finally {
      setCreatingInst(false);
    }
  };

  useEffect(() => {
    // Check if user is super_admin
    if (user?.role !== 'super_admin') {
      toast.error('No tienes permisos para acceder a esta página');
      return;
    }
    
    fetchData();
  }, [user]);

  useEffect(() => {
    if (institutions.length > 0) {
      fetchUsers();
    }
  }, [selectedInstitution, selectedRole, institutions.length]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [institutionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/institutions`, { withCredentials: true }),
        axios.get(`${API}/admin/global/stats`, { withCredentials: true })
      ]);
      
      setInstitutions(institutionsRes.data);
      setStats(statsRes.data);
      
      // Fetch users after institutions are loaded
      await fetchUsers();
      await fetchTrash();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedInstitution !== 'all') {
        params.append('institution_id', selectedInstitution);
      }
      if (selectedRole !== 'all') {
        params.append('role', selectedRole);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await axios.get(
        `${API}/admin/global/users?${params.toString()}`,
        { withCredentials: true }
      );
      
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar usuarios');
    }
  };

  const handleSearch = () => {
    fetchUsers();
  };

  const handleToggleActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Estás seguro de ${action} este usuario?`)) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { is_active: !currentStatus },
        { withCredentials: true }
      );
      toast.success(`Usuario ${action === 'desactivar' ? 'desactivado' : 'activado'}`);
      fetchUsers();
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al ${action} usuario`);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    
    if (!inviteForm.institution_id) {
      toast.error('Por favor selecciona una institución');
      return;
    }

    setInviting(true);
    try {
      const response = await axios.post(
        `${API}/invitations/invite`,
        {
          name: inviteForm.name,
          email: inviteForm.email,
          role: inviteForm.role,
          send_email: inviteForm.send_email,
          phone: inviteForm.phone,
          institution_id: inviteForm.institution_id
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message || 'Invitación enviada correctamente');
      setInviteDialogOpen(false);
      if (response.data.token) {
        setCreatedInvite({ id: response.data.id, token: response.data.token, email: inviteForm.email, name: inviteForm.name, phone: inviteForm.phone });
      }
      setInviteForm({
        name: '',
        email: '',
        role: 'therapist',
        institution_id: '',
        phone: '',
        send_email: true
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar invitación');
    } finally {
      setInviting(false);
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Acceso Denegado</h2>
          <p className="text-gray-600">
            Esta página es exclusiva para Super Administradores
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p>Cargando panel global...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="h-8 w-8" style={{ color: '#5A7D6C' }} />
          <h1 className="text-3xl font-bold" style={{ color: '#2D3748' }}>
            Panel de Administración Global
          </h1>
        </div>
        <p className="text-gray-600">
          Vista consolidada de todas las instituciones y usuarios del sistema
        </p>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Instituciones</p>
                <p className="text-2xl font-bold">{stats.total_institutions}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Building2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Usuarios</p>
                <p className="text-2xl font-bold">{stats.total_users}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Terapeutas</p>
                <p className="text-2xl font-bold">{stats.total_therapists}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pacientes</p>
                <p className="text-2xl font-bold">{stats.total_patients}</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Institutions Management */}
      <Card className="p-6 mb-6" data-testid="institutions-management">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5" style={{ color: '#5A7D6C' }} />
          <h2 className="text-lg font-semibold">Instituciones ({institutions.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Institución</th>
                <th className="text-left py-3 px-4">Usuarios</th>
                <th className="text-left py-3 px-4">Estado</th>
                <th className="text-left py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {institutions.map((inst) => {
                const isActive = inst.is_active !== false;
                const isOwn = user?.institution_id === inst.id;
                const isPrincipal = inst.name === 'Administración Principal';
                return (
                  <tr key={inst.id} className="border-b hover:bg-gray-50" data-testid={`institution-row-${inst.id}`}>
                    <td className="py-3 px-4 font-medium">{inst.name}</td>
                    <td className="py-3 px-4 text-gray-600">{inst.user_count}</td>
                    <td className="py-3 px-4">
                      {isActive ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Power className="h-3 w-3 mr-1" /> Activa
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">
                          <PowerOff className="h-3 w-3 mr-1" /> Inactiva
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleInstitution(inst)}
                          disabled={isOwn}
                          title={isOwn ? 'No puedes cambiar tu propia institución' : ''}
                          data-testid={`toggle-institution-${inst.id}`}
                        >
                          {isActive ? (
                            <><PowerOff className="h-4 w-4 mr-1" />Desactivar</>
                          ) : (
                            <><Power className="h-4 w-4 mr-1" />Activar</>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => { setDeleteInstDialog({ open: true, inst }); setDeleteConfirmName(''); }}
                          disabled={isOwn || isPrincipal}
                          title={isOwn || isPrincipal ? 'Esta institución no se puede eliminar' : ''}
                          data-testid={`delete-institution-${inst.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recycle Bin */}
      {trash.length > 0 && (
        <Card className="p-6 mb-6 border-amber-200" data-testid="institutions-trash">
          <div className="flex items-center gap-2 mb-1">
            <Trash2 className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Papelera de Instituciones ({trash.length})</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Las instituciones eliminadas se conservan 30 días y luego se borran definitivamente de forma automática.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Institución</th>
                  <th className="text-left py-3 px-4">Usuarios</th>
                  <th className="text-left py-3 px-4">Eliminada</th>
                  <th className="text-left py-3 px-4">Días restantes</th>
                  <th className="text-left py-3 px-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {trash.map((inst) => (
                  <tr key={inst.id} className="border-b hover:bg-amber-50/40" data-testid={`trash-row-${inst.id}`}>
                    <td className="py-3 px-4 font-medium">{inst.name}</td>
                    <td className="py-3 px-4 text-gray-600">{inst.user_count}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {inst.deleted_at ? new Date(inst.deleted_at).toLocaleDateString('es-ES') : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={inst.days_remaining <= 5 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                        {inst.days_remaining ?? 0} días
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => handleRestoreInstitution(inst)}
                          data-testid={`restore-institution-${inst.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restaurar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => { setPurgeDialog({ open: true, inst }); setPurgeConfirmName(''); }}
                          data-testid={`purge-institution-${inst.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold">Filtros</h2>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.open(`${API}/manual/invitations/download`, '_blank')}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="download-invite-guide-btn"
            >
              <FileText className="h-4 w-4" />
              Guía de Invitación (PDF)
            </Button>
            <Button
              onClick={() => setInstDialogOpen(true)}
              variant="outline"
              className="flex items-center gap-2"
              style={{ borderColor: '#5A7D6C', color: '#5A7D6C' }}
              data-testid="create-institution-btn"
            >
              <Building2 className="h-4 w-4" />
              Crear Nueva Institución
            </Button>
            <Button 
              onClick={() => setInviteDialogOpen(true)}
              className="flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow"
              style={{ backgroundColor: '#5A7D6C' }}
              data-testid="invite-professional-btn"
            >
              <Plus className="h-4 w-4" />
              Invitar Nuevo Profesional
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Institution Filter */}
          <div>
            <Label>Institución</Label>
            <Select value={selectedInstitution} onValueChange={setSelectedInstitution}>
              <SelectTrigger>
                <SelectValue placeholder="Todas las instituciones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las instituciones</SelectItem>
                {institutions.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id}>
                    {inst.name} ({inst.user_count} usuarios)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Role Filter */}
          <div>
            <Label>Rol</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="Todos los roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="therapist">Terapeutas</SelectItem>
                <SelectItem value="admin">Administradores</SelectItem>
                <SelectItem value="read_only">Solo Lectura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search */}
          <div>
            <Label>Buscar</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            Usuarios Profesionales ({users.length})
          </h2>
          <Badge variant="outline" className="text-sm">
            {selectedInstitution === 'all' 
              ? 'Todas las instituciones' 
              : institutions.find(i => i.id === selectedInstitution)?.name || 'Filtrado'
            }
          </Badge>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Nombre</th>
                <th className="text-left py-3 px-4">Email</th>
                <th className="text-left py-3 px-4">Institución</th>
                <th className="text-left py-3 px-4">Rol</th>
                <th className="text-left py-3 px-4">Estado</th>
                <th className="text-left py-3 px-4">Fecha Registro</th>
                <th className="text-left py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((usr) => {
                const roleInfo = ROLE_INFO[usr.role] || {};
                const isActive = usr.is_active !== false;
                
                return (
                  <tr key={usr.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{usr.name}</td>
                    <td className="py-3 px-4 text-gray-600">{usr.email}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-500" />
                        <span className="text-sm">{usr.institution_name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={roleInfo.color}>
                        {roleInfo.label || usr.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {isActive ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Power className="h-3 w-3 mr-1" />
                          Activo
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">
                          <PowerOff className="h-3 w-3 mr-1" />
                          Inactivo
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {usr.created_at ? new Date(usr.created_at).toLocaleDateString('es-ES') : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        variant={isActive ? "outline" : "default"}
                        size="sm"
                        onClick={() => handleToggleActive(usr.id, isActive)}
                      >
                        {isActive ? (
                          <>
                            <PowerOff className="h-4 w-4 mr-1" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-1" />
                            Activar
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {users.length === 0 && (
            <div className="text-center py-12">
              <UserX className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">
                No se encontraron usuarios con los filtros seleccionados
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Administradores</p>
              <p className="text-xl font-bold">{stats?.total_admins || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <UserX className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-sm text-gray-600">Usuarios Inactivos</p>
              <p className="text-xl font-bold">{stats?.inactive_users || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Total Informes</p>
              <p className="text-xl font-bold">{stats?.total_reports || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Purge (permanent delete) Dialog */}
      <Dialog open={purgeDialog.open} onOpenChange={(open) => { if (!open) { setPurgeDialog({ open: false, inst: null }); setPurgeConfirmName(''); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Definitivamente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                Esta acción es <strong>irreversible</strong>. Se borrarán permanentemente la institución
                <strong> "{purgeDialog.inst?.name}"</strong> y TODOS sus datos: usuarios, pacientes,
                informes, citas y recursos. No podrás restaurarla.
              </p>
            </div>
            <div>
              <Label htmlFor="purge-confirm-name">
                Para confirmar, escribe el nombre exacto de la institución:
              </Label>
              <Input
                id="purge-confirm-name"
                value={purgeConfirmName}
                onChange={(e) => setPurgeConfirmName(e.target.value)}
                placeholder={purgeDialog.inst?.name}
                className="mt-1"
                data-testid="purge-confirm-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setPurgeDialog({ open: false, inst: null }); setPurgeConfirmName(''); }}
                className="flex-1"
                disabled={purging}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handlePurgeInstitution}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={purging || purgeConfirmName.trim() !== (purgeDialog.inst?.name || '').trim()}
                data-testid="purge-institution-confirm-btn"
              >
                {purging ? 'Eliminando...' : 'Eliminar Definitivamente'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Institution Dialog */}
      <Dialog open={deleteInstDialog.open} onOpenChange={(open) => { if (!open) { setDeleteInstDialog({ open: false, inst: null }); setDeleteConfirmName(''); } }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Institución
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                La institución <strong>"{deleteInstDialog.inst?.name}"</strong> se moverá a la
                <strong> papelera durante 30 días</strong>. Sus usuarios no podrán iniciar sesión, pero
                podrás <strong>restaurarla</strong> en cualquier momento antes de que se elimine definitivamente.
              </p>
            </div>
            <div>
              <Label htmlFor="confirm-name">
                Para confirmar, escribe el nombre exacto de la institución:
              </Label>
              <Input
                id="confirm-name"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={deleteInstDialog.inst?.name}
                className="mt-1"
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDeleteInstDialog({ open: false, inst: null }); setDeleteConfirmName(''); }}
                className="flex-1"
                disabled={deletingInst}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDeleteInstitution}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={deletingInst || deleteConfirmName.trim() !== (deleteInstDialog.inst?.name || '').trim()}
                data-testid="delete-institution-confirm-btn"
              >
                {deletingInst ? 'Moviendo...' : 'Mover a la Papelera'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invitation Created - Manual Share Dialog */}
      <Dialog open={!!createdInvite} onOpenChange={(open) => { if (!open) setCreatedInvite(null); }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="invite-code-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" style={{ color: '#5A7D6C' }} />
              Invitación creada
            </DialogTitle>
          </DialogHeader>
          {createdInvite && (() => {
            const inviteLink = `${window.location.origin}/accept-invite?token=${createdInvite.token}`;
            const shareMessage = `Hola ${createdInvite.name}, has sido invitado/a a REKORD. Crea tu cuenta aquí: ${inviteLink} (o usa el código: ${createdInvite.token})`;
            return (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Comparte estos datos con <strong>{createdInvite.name}</strong> ({createdInvite.email}) por
                  WhatsApp, email o el medio que prefieras.
                </p>
                <div>
                  <Label className="text-xs text-gray-500">Enlace de invitación</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={inviteLink} className="font-mono text-xs" data-testid="invite-link-input" />
                    <Button type="button" variant="outline" onClick={() => copyToClipboard(inviteLink)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Código de invitación</Label>
                  <div className="flex gap-2 mt-1">
                    <Input readOnly value={createdInvite.token} className="font-mono text-xs" data-testid="invite-code-input" />
                    <Button type="button" variant="outline" onClick={() => copyToClipboard(createdInvite.token)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-xs text-emerald-800 mb-2">Mensaje listo para enviar:</p>
                  <p className="text-xs text-emerald-900">{shareMessage}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button type="button" size="sm" style={{ backgroundColor: '#25D366' }}
                      onClick={() => { window.open(buildWhatsappUrl(createdInvite.phone, shareMessage), '_blank'); markShared(createdInvite.id); }}
                      data-testid="whatsapp-share-btn">
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Enviar por WhatsApp
                    </Button>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => { copyToClipboard(shareMessage); markShared(createdInvite.id); }} data-testid="copy-share-message-btn">
                      <Copy className="h-4 w-4 mr-1" />
                      Copiar mensaje
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col items-center p-3 border rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">O que lo escanee desde su móvil:</p>
                  <div className="bg-white p-2 rounded" data-testid="invite-qr">
                    <QRCodeCanvas value={inviteLink} size={140} includeMargin />
                  </div>
                </div>
                <p className="text-xs text-gray-400">Válido 7 días. Guarda esto ahora: el código no se vuelve a mostrar.</p>
                <Button type="button" variant="outline" className="w-full" onClick={() => setCreatedInvite(null)}>
                  Cerrar
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Create Institution Dialog */}
      <Dialog open={instDialogOpen} onOpenChange={setInstDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" style={{ color: '#5A7D6C' }} />
              Crear Nueva Institución
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateInstitution} className="space-y-4 mt-4">
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-800">
                Se creará una institución totalmente aislada (con su propio identificador) y su primer
                Administrador. Los datos de esta institución nunca se mezclarán con otras.
              </p>
            </div>

            <div>
              <Label htmlFor="inst-name">Nombre de la Institución *</Label>
              <Input
                id="inst-name"
                value={instForm.name}
                onChange={(e) => setInstForm({ ...instForm, name: e.target.value })}
                placeholder="Ej: Centro Multisensorial Aurora"
                required
                data-testid="inst-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="inst-address">Dirección</Label>
                <Input
                  id="inst-address"
                  value={instForm.address}
                  onChange={(e) => setInstForm({ ...instForm, address: e.target.value })}
                  placeholder="Calle, ciudad"
                />
              </div>
              <div>
                <Label htmlFor="inst-phone">Teléfono</Label>
                <Input
                  id="inst-phone"
                  value={instForm.phone}
                  onChange={(e) => setInstForm({ ...instForm, phone: e.target.value })}
                  placeholder="+34 ..."
                />
              </div>
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm font-semibold text-gray-700 mb-2">Administrador de la institución</p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="admin-name">Nombre del Administrador *</Label>
                  <Input
                    id="admin-name"
                    value={instForm.admin_name}
                    onChange={(e) => setInstForm({ ...instForm, admin_name: e.target.value })}
                    placeholder="Ej: Laura Martínez"
                    required
                    data-testid="inst-admin-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-email">Email del Administrador *</Label>
                  <Input
                    id="admin-email"
                    type="email"
                    value={instForm.admin_email}
                    onChange={(e) => setInstForm({ ...instForm, admin_email: e.target.value })}
                    placeholder="admin@institucion.com"
                    required
                    data-testid="inst-admin-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password">Contraseña inicial *</Label>
                  <Input
                    id="admin-password"
                    type="text"
                    value={instForm.admin_password}
                    onChange={(e) => setInstForm({ ...instForm, admin_password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                    data-testid="inst-admin-password-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comparte esta contraseña con el administrador. Podrá cambiarla al iniciar sesión.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setInstDialogOpen(false)}
                className="flex-1"
                disabled={creatingInst}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1"
                style={{ backgroundColor: '#5A7D6C' }}
                disabled={creatingInst}
                data-testid="inst-submit-btn"
              >
                {creatingInst ? 'Creando...' : 'Crear Institución'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" style={{ color: '#5A7D6C' }} />
              Invitar Nuevo Profesional
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleInviteUser} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Nombre Completo *</Label>
              <Input
                id="name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Ej: María González"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="ejemplo@correo.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">Teléfono / WhatsApp (opcional)</Label>
              <Input
                id="phone"
                value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="+54 9 11 1234 5678"
                data-testid="invite-phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">Con código de país para abrir WhatsApp directo en su chat.</p>
            </div>

            <div>
              <Label htmlFor="institution">Institución *</Label>
              <Select
                value={inviteForm.institution_id}
                onValueChange={(value) => setInviteForm({ ...inviteForm, institution_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una institución" />
                </SelectTrigger>
                <SelectContent>
                  {institutions.map((inst) => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="role">Rol *</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="therapist">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>Terapeuta</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <span>Administrador</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="read_only">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      <span>Solo Lectura</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {inviteForm.role === 'therapist' && 'Acceso completo a pacientes y reportes'}
                {inviteForm.role === 'admin' && 'Gestión completa de la institución'}
                {inviteForm.role === 'read_only' && 'Ver datos sin editar'}
              </p>
            </div>

            <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <input
                type="checkbox"
                id="send_email"
                checked={inviteForm.send_email}
                onChange={(e) => setInviteForm({ ...inviteForm, send_email: e.target.checked })}
                className="rounded h-4 w-4"
              />
              <div className="flex-1">
                <Label htmlFor="send_email" className="font-medium text-blue-900 cursor-pointer">
                  Enviar invitación por email automáticamente
                </Label>
                <p className="text-xs text-blue-700 mt-0.5">
                  El profesional recibirá un correo con su código de invitación via Resend
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setInviteDialogOpen(false)}
                className="flex-1"
                disabled={inviting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1"
                style={{ backgroundColor: '#5A7D6C' }}
                disabled={inviting}
              >
                {inviting ? 'Enviando...' : 'Enviar Invitación'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GlobalAdminPanelPage;
